import React, { useState } from 'react';
import { X, ShieldCheck, Loader2, ExternalLink, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../services/api/client';
import { ENDPOINTS } from '../../../services/api/endpoints';

const MARKETPLACE_OPTIONS = [
  { id: 'A21TJRUUN4KGV', label: 'India (IN)', flag: 'IN' },
  { id: 'ATVPDKIKX0DER', label: 'United States (US)', flag: 'US' },
  { id: 'A1F83G8C2ARO7P', label: 'United Kingdom (UK)', flag: 'GB' },
  { id: 'A1PA6795UKMFR9', label: 'Germany (DE)', flag: 'DE' },
  { id: 'A1VC38T7YXB528', label: 'Japan (JP)', flag: 'JP' },
];

export default function AmazonConnectModal({ isOpen, onClose, onConnect, sellerId }) {
  const [mode, setMode] = useState('oauth'); // 'oauth' or 'manual'
  const [marketplaceId, setMarketplaceId] = useState('A21TJRUUN4KGV');
  const [authorizing, setAuthorizing] = useState(false);

  // Manual mode state
  const [refreshToken, setRefreshToken] = useState('');
  const [sellingPartnerId, setSellingPartnerId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleOAuthAuthorize = async () => {
    setAuthorizing(true);
    try {
      const res = await apiClient.get(
        `${ENDPOINTS.AMAZON_OAUTH.AUTHORIZE}?sellerId=${sellerId}&marketplaceId=${marketplaceId}`
      );
      const data = res.data || res;
      if (data.authorizationUrl) {
        // Open Amazon in a new tab
        const authWindow = window.open(data.authorizationUrl, '_blank');
        // Listen for callback completion via storage event
        const handleStorage = (e) => {
          if (e.key === 'amazon_oauth_complete') {
            window.removeEventListener('storage', handleStorage);
            localStorage.removeItem('amazon_oauth_complete');
            setAuthorizing(false);
            onClose();
            // Trigger parent refresh
            if (typeof onConnect === 'function') {
              onConnect({ _oauthComplete: true });
            }
            toast.success('Amazon account connected successfully!');
          }
        };
        window.addEventListener('storage', handleStorage);
        // Reset authorizing after 5 min timeout (in case user closes the tab)
        setTimeout(() => {
          window.removeEventListener('storage', handleStorage);
          setAuthorizing(false);
        }, 300000);
      } else {
        toast.error('Failed to generate authorization URL');
        setAuthorizing(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to start authorization');
      setAuthorizing(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!refreshToken.trim() || !sellingPartnerId.trim()) return;
    setSubmitting(true);
    try {
      await onConnect({
        sellerId,
        platform: 'AMAZON',
        refreshToken: refreshToken.trim(),
        sellingPartnerId: sellingPartnerId.trim(),
        marketplaceId,
      });
      setRefreshToken('');
      setSellingPartnerId('');
      setMarketplaceId('A21TJRUUN4KGV');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-orange-50 to-orange-100/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Connect Amazon</h3>
              <p className="text-xs text-gray-500">Link your Amazon Seller Central account</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Marketplace selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Marketplace</label>
            <select
              value={marketplaceId}
              onChange={(e) => setMarketplaceId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
            >
              {MARKETPLACE_OPTIONS.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* OAuth - Primary method */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-orange-800 mb-2">Authorize via Amazon</h4>
            <p className="text-xs text-orange-600 mb-4">
              Click the button below to securely connect your Amazon account. You'll be redirected to
              Amazon Seller Central to grant access — no tokens to copy-paste.
            </p>
            {authorizing ? (
              <div className="w-full text-center py-5 bg-orange-50 border border-orange-200 rounded-lg">
                <Loader2 size={22} className="animate-spin text-orange-500 mx-auto mb-3" />
                <p className="text-base font-semibold text-orange-800">Almost there!</p>
                <p className="text-sm text-orange-600 mt-1.5 leading-relaxed">
                  We've opened Amazon Seller Central in a new tab.<br />
                  Please log in and authorize eComm Notify.
                </p>
                <p className="text-xs text-orange-400 mt-3">We'll connect your account automatically once done.</p>
                <button onClick={() => setAuthorizing(false)} className="text-xs text-orange-400 hover:text-orange-600 mt-3 underline">Cancel</button>
              </div>
            ) : (
              <button
                onClick={handleOAuthAuthorize}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors shadow-sm"
              >
                <ExternalLink size={16} />
                Authorize with Amazon
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center">
              <button
                onClick={() => setMode(mode === 'manual' ? 'oauth' : 'manual')}
                className="bg-white px-3 text-xs font-medium text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
              >
                Manual Token Input
                <ChevronDown size={12} className={`transition-transform ${mode === 'manual' ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Manual - Fallback */}
          {mode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Refresh Token <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={refreshToken}
                  onChange={(e) => setRefreshToken(e.target.value)}
                  placeholder="Atzr|IwEB..."
                  rows={3}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Selling Partner ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={sellingPartnerId}
                  onChange={(e) => setSellingPartnerId(e.target.value)}
                  placeholder="e.g., A2FGR..."
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
              </div>
              <div className="flex items-start gap-3 p-3.5 bg-teal-50 border border-teal-200 rounded-lg">
                <ShieldCheck size={18} className="text-teal-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-teal-800">Encrypted Storage</p>
                  <p className="text-xs text-teal-600 mt-0.5">
                    Your token is encrypted with AES-256 and never exposed in API responses.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={onClose}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit"
                  disabled={submitting || !refreshToken.trim() || !sellingPartnerId.trim()}
                  className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                  {submitting ? 'Connecting...' : 'Connect Manually'}
                </button>
              </div>
            </form>
          )}

          {/* Cancel for OAuth mode */}
          {mode !== 'manual' && (
            <div className="flex justify-end">
              <button onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
